import { useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import { DateRange } from "react-day-picker";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  Target,
  AlertTriangle,
  CheckCircle,
  Clock,
  Download,
  Filter,
  RefreshCw,
} from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { Progress } from "@/components/ui/progress";
import Alert from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";

// Chart components (using a simple chart library or custom implementation)
import { AttendanceTrendChart } from "./charts/AttendanceTrendChart";
import { MonthlyBreakdownChart } from "./charts/MonthlyBreakdownChart";
import { MemberPerformanceChart } from "./charts/MemberPerformanceChart";
import { ComplianceChart } from "./charts/ComplianceChart";

interface AnalyticsData {
  overview: {
    totalMeetings: number;
    completedMeetings: number;
    totalTeachers: number;
    averageAttendance: number;
    completionRate: number;
  };
  trends: {
    current: number;
    previous: number;
    direction: 'improving' | 'declining' | 'stable';
    change: number;
  };
  teacherPerformance: Array<{
    teacherId: number;
    teacherName: string;
    totalMeetings: number;
    attendedMeetings: number;
    attendanceRate: number;
    performance: 'excellent' | 'good' | 'fair' | 'poor';
  }>;
  monthlyBreakdown: Array<{
    month: string;
    totalMeetings: number;
    totalAttendance: number;
    averageAttendance: number;
  }>;
  insights: Array<{
    type: 'positive' | 'neutral' | 'warning';
    message: string;
    recommendation: string;
  }>;
}

interface ForecastData {
  forecast: {
    predictedAttendance: number;
    confidenceInterval: {
      lower: number;
      upper: number;
    };
    confidenceLevel: number;
  };
  factors: {
    historicalAverage: number;
    standardDeviation: number;
    sampleSize: number;
    forecastPeriod: number;
  };
  recommendations: string[];
}

export default function AdvancedAnalyticsDashboard() {
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: startOfMonth(subDays(new Date(), 30)),
    to: endOfMonth(new Date())
  });
  
  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from && range?.to) {
      setDateRange({ from: range.from, to: range.to });
    }
  };
  const [selectedPeriod, setSelectedPeriod] = useState("30d");
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch analytics data
  const { data: analyticsData, isLoading: analyticsLoading, refetch: refetchAnalytics } = useQuery<AnalyticsData>(
    ["analytics", dateRange, selectedPeriod],
    async () => {
      const response = await api.get("/api/attendance/analytics", {
        params: {
          startDate: format(dateRange.from, "yyyy-MM-dd"),
          endDate: format(dateRange.to, "yyyy-MM-dd")
        }
      });
      return response.data.data;
    },
    {
      refetchInterval: 300000, // Refetch every 5 minutes
      staleTime: 60000 // Consider data stale after 1 minute
    }
  );

  // Fetch forecast data
  const { data: forecastData, isLoading: forecastLoading } = useQuery<ForecastData>(
    ["forecast", selectedPeriod],
    async () => {
      const response = await api.get("/api/attendance/forecast", {
        params: {
          forecastDays: selectedPeriod === "30d" ? 30 : selectedPeriod === "90d" ? 90 : 7,
          confidenceLevel: 0.95
        }
      });
      return response.data.data;
    }
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetchAnalytics();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const handleExportReport = async (reportFormat: 'csv' | 'pdf') => {
    try {
      const response = await api.post("/api/attendance/reports/generate", {
        startDate: format(dateRange.from, "yyyy-MM-dd"),
        endDate: format(dateRange.to, "yyyy-MM-dd"),
        reportType: 'detailed',
        format: reportFormat,
        includeDetails: true
      });

      if (reportFormat === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attendance-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        // PDF export would be handled differently
        console.log('PDF export:', response.data);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  if (analyticsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const analytics = analyticsData || {
    overview: { totalMeetings: 0, completedMeetings: 0, totalTeachers: 0, averageAttendance: 0, completionRate: 0 },
    trends: { current: 0, previous: 0, direction: 'stable', change: 0 },
    teacherPerformance: [],
    monthlyBreakdown: [],
    insights: []
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fffffb' }}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#050500' }}>
              Advanced Analytics Dashboard
            </h1>
            <p className="text-lg" style={{ color: '#050500', opacity: 0.7 }}>
              Comprehensive attendance analytics and insights
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExportReport('csv')}
              disabled={isRefreshing}
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExportReport('pdf')}
              disabled={isRefreshing}
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Analytics Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Period:</label>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                    <SelectItem value="custom">Custom range</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {selectedPeriod === "custom" && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Date Range:</label>
                  <DatePickerWithRange
                    date={dateRange}
                    onDateChange={handleDateRangeChange}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Total Meetings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.overview.totalMeetings}</div>
              <p className="text-xs text-muted-foreground">
                {analytics.overview.completedMeetings} completed
              </p>
              <Progress 
                value={analytics.overview.completionRate} 
                className="mt-2 h-2"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Average Attendance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {analytics.overview.averageAttendance}%
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                {analytics.trends.direction === 'improving' && <TrendingUp className="w-3 h-3 mr-1 text-green-600" />}
                {analytics.trends.direction === 'declining' && <TrendingDown className="w-3 h-3 mr-1 text-red-600" />}
                {analytics.trends.direction === 'stable' && <Clock className="w-3 h-3 mr-1 text-gray-600" />}
                {analytics.trends.change > 0 ? '+' : ''}{analytics.trends.change}% vs previous period
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Target className="w-4 h-4" />
                Completion Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {analytics.overview.completionRate}%
              </div>
              <p className="text-xs text-muted-foreground">
                Meetings marked as conducted
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Total Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {analytics.overview.totalTeachers}
              </div>
              <p className="text-xs text-muted-foreground">
                Active members
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Insights and Alerts */}
        {analytics.insights.length > 0 && (
          <div className="space-y-3">
            {analytics.insights.map((insight) => (
              <Alert>
                <div className="flex items-start gap-2">
                  {insight.type === 'positive' && <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />}
                  {insight.type === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />}
                  {insight.type === 'neutral' && <BarChart3 className="w-4 h-4 text-blue-600 mt-0.5" />}
                  <div>
                    <div className="font-medium">
                      {insight.message}
                    </div>
                    <div className="text-sm mt-1">
                      <strong>Recommendation:</strong> {insight.recommendation}
                    </div>
                  </div>
                </div>
              </Alert>
            ))}
          </div>
        )}

        {/* Main Analytics Tabs */}
        <Tabs defaultValue="trends" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="compliance">Compliance</TabsTrigger>
            <TabsTrigger value="forecast">Forecast</TabsTrigger>
          </TabsList>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Attendance Trends</CardTitle>
                  <CardDescription>Monthly attendance patterns and trends</CardDescription>
                </CardHeader>
                <CardContent>
                  <AttendanceTrendChart data={analytics.monthlyBreakdown} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Monthly Breakdown</CardTitle>
                  <CardDescription>Detailed monthly attendance statistics</CardDescription>
                </CardHeader>
                <CardContent>
                  <MonthlyBreakdownChart data={analytics.monthlyBreakdown} />
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Trend Analysis</CardTitle>
                <CardDescription>Detailed analysis of attendance patterns</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 border rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {analytics.trends.current}%
                      </div>
                      <div className="text-sm text-muted-foreground">Current Period</div>
                    </div>
                    <div className="text-center p-4 border rounded-lg">
                      <div className="text-2xl font-bold text-gray-600">
                        {analytics.trends.previous}%
                      </div>
                      <div className="text-sm text-muted-foreground">Previous Period</div>
                    </div>
                    <div className="text-center p-4 border rounded-lg">
                      <div className={`text-2xl font-bold ${
                        analytics.trends.change > 0 ? 'text-green-600' : 
                        analytics.trends.change < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {analytics.trends.change > 0 ? '+' : ''}{analytics.trends.change}%
                      </div>
                      <div className="text-sm text-muted-foreground">Change</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Member Performance Analysis</CardTitle>
                <CardDescription>Individual member attendance performance</CardDescription>
              </CardHeader>
              <CardContent>
                <MemberPerformanceChart data={analytics.teacherPerformance} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Performance Rankings</CardTitle>
                <CardDescription>Top and bottom performers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-green-600 mb-3">Top Performers</h4>
                    <ScrollArea className="h-64">
                      <div className="space-y-2">
                        {analytics.teacherPerformance
                          .sort((a, b) => b.attendanceRate - a.attendanceRate)
                          .slice(0, 5)
                          .map((teacher, index) => (
                            <div key={teacher.teacherId} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="flex items-center gap-3">
                                <Badge variant="outline" className="w-6 h-6 rounded-full flex items-center justify-center text-xs">
                                  {index + 1}
                                </Badge>
                                <div>
                                  <div className="font-medium">{teacher.teacherName}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {teacher.attendedMeetings}/{teacher.totalMeetings} meetings
                                  </div>
                                </div>
                              </div>
                              <Badge variant="default" className="bg-green-100 text-green-800">
                                {teacher.attendanceRate}%
                              </Badge>
                            </div>
                          ))}
                      </div>
                    </ScrollArea>
                  </div>

                  <div>
                    <h4 className="font-semibold text-red-600 mb-3">Needs Improvement</h4>
                    <ScrollArea className="h-64">
                      <div className="space-y-2">
                        {analytics.teacherPerformance
                          .sort((a, b) => a.attendanceRate - b.attendanceRate)
                          .slice(0, 5)
                          .map((teacher, index) => (
                            <div key={teacher.teacherId} className="flex items-center justify-between p-3 border rounded-lg">
                              <div className="flex items-center gap-3">
                                <Badge variant="outline" className="w-6 h-6 rounded-full flex items-center justify-center text-xs">
                                  {index + 1}
                                </Badge>
                                <div>
                                  <div className="font-medium">{teacher.teacherName}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {teacher.attendedMeetings}/{teacher.totalMeetings} meetings
                                  </div>
                                </div>
                              </div>
                              <Badge variant="destructive">
                                {teacher.attendanceRate}%
                              </Badge>
                            </div>
                          ))}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Compliance Overview</CardTitle>
                <CardDescription>Attendance compliance metrics and thresholds</CardDescription>
              </CardHeader>
              <CardContent>
                <ComplianceChart data={analytics.teacherPerformance} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Forecast Tab */}
          <TabsContent value="forecast" className="space-y-4">
            {forecastLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : forecastData ? (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Attendance Forecast</CardTitle>
                    <CardDescription>Predicted attendance rates for the next period</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center p-6 border rounded-lg">
                        <div className="text-3xl font-bold text-blue-600">
                          {forecastData.forecast.predictedAttendance}%
                        </div>
                        <div className="text-sm text-muted-foreground">Predicted Attendance</div>
                        <div className="text-xs text-muted-foreground mt-2">
                          Confidence: {forecastData.forecast.confidenceLevel}%
                        </div>
                      </div>
                      <div className="text-center p-6 border rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                          {forecastData.forecast.confidenceInterval.upper}%
                        </div>
                        <div className="text-sm text-muted-foreground">Upper Bound</div>
                      </div>
                      <div className="text-center p-6 border rounded-lg">
                        <div className="text-2xl font-bold text-red-600">
                          {forecastData.forecast.confidenceInterval.lower}%
                        </div>
                        <div className="text-sm text-muted-foreground">Lower Bound</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Forecast Factors</CardTitle>
                    <CardDescription>Key factors influencing the prediction</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">Historical Average:</span>
                          <span className="text-sm">{forecastData.factors.historicalAverage}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">Standard Deviation:</span>
                          <span className="text-sm">{forecastData.factors.standardDeviation.toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">Sample Size:</span>
                          <span className="text-sm">{forecastData.factors.sampleSize} meetings</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">Forecast Period:</span>
                          <span className="text-sm">{forecastData.factors.forecastPeriod} days</span>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Recommendations</h4>
                        <ul className="space-y-1 text-sm">
                          {forecastData.recommendations.map((rec, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-blue-600">•</span>
                              <span>{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No forecast data available</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
